import { useState } from 'react'
import { invoicesService } from '@/services/invoices-service'

interface CreateInvoiceModalProps {
  proposalId: string
  proposalTitle: string
  estimatedValue: number
  open: boolean
  onClose: () => void
  onCreated: (invoiceId: string) => void
}

type InvoiceType = 'full' | 'deposit' | 'progress' | 'retention'
type PaymentTerms = 'due_on_receipt' | 'net_15' | 'net_30' | 'net_45' | 'net_60'

export function CreateInvoiceModal({
  proposalId,
  proposalTitle: _proposalTitle,
  estimatedValue,
  open,
  onClose,
  onCreated,
}: CreateInvoiceModalProps) {
  void _proposalTitle
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('full')
  const [depositPercent, setDepositPercent] = useState(50)
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>('net_30')
  const [taxRate, setTaxRate] = useState(0)
  const [notes, setNotes] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const calculateAmount = (): number => {
    switch (invoiceType) {
      case 'full':
        return estimatedValue
      case 'deposit':
        return estimatedValue * (depositPercent / 100)
      case 'progress':
      case 'retention':
        return estimatedValue * (depositPercent / 100)
      default:
        return estimatedValue
    }
  }

  const baseAmount = calculateAmount()
  const taxAmount = baseAmount * (taxRate / 100)
  const totalAmount = baseAmount + taxAmount

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const invoice = await invoicesService.create({
        proposalId,
        invoiceType,
        depositPercent: invoiceType === 'deposit' ? depositPercent : undefined,
        paymentTerms,
        taxRate,
        notes: notes || undefined,
      })

      onCreated(invoice.id)
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setInvoiceType('full')
    setDepositPercent(50)
    setPaymentTerms('net_30')
    setTaxRate(0)
    setNotes('')
    setError(null)
    onClose()
  }

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  return (
    <div className="inv-modal-overlay" onMouseDown={handleBackdropMouseDown} onKeyDown={handleKeyDown}>
      <div className="inv-modal">
        <div className="inv-modal-header">
          <h2>Create Invoice</h2>
          <button
            onClick={handleClose}
            className="inv-modal-close"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="inv-modal-body">
          {error && (
            <div className="inv-modal-error" role="alert">
              {error}
            </div>
          )}

          <div className="inv-modal-field">
            <label className="inv-modal-label">Invoice Type</label>
            <div className="inv-modal-radio-group">
              {(['full', 'deposit', 'progress', 'retention'] as InvoiceType[]).map((type) => (
                <label key={type} className="inv-modal-radio">
                  <input
                    type="radio"
                    name="invoice-type"
                    value={type}
                    checked={invoiceType === type}
                    onChange={(e) => setInvoiceType(e.target.value as InvoiceType)}
                  />
                  <span className="inv-modal-radio-label">
                    {type === 'full' && 'Full Invoice'}
                    {type === 'deposit' && 'Deposit'}
                    {type === 'progress' && 'Progress Payment'}
                    {type === 'retention' && 'Retention'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {invoiceType === 'deposit' && (
            <div className="inv-modal-field">
              <label htmlFor="deposit-percent" className="inv-modal-label">
                Deposit Percentage
              </label>
              <input
                id="deposit-percent"
                type="number"
                min="1"
                max="100"
                value={depositPercent}
                onChange={(e) => setDepositPercent(Number(e.target.value))}
                className="inv-modal-input"
              />
            </div>
          )}

          {(invoiceType === 'progress' || invoiceType === 'retention') && (
            <div className="inv-modal-field">
              <label htmlFor="percent-amount" className="inv-modal-label">
                Percentage of Total
              </label>
              <input
                id="percent-amount"
                type="number"
                min="1"
                max="100"
                value={depositPercent}
                onChange={(e) => setDepositPercent(Number(e.target.value))}
                className="inv-modal-input"
              />
            </div>
          )}

          <div className="inv-modal-field">
            <label htmlFor="payment-terms" className="inv-modal-label">
              Payment Terms
            </label>
            <select
              id="payment-terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value as PaymentTerms)}
              className="inv-modal-select"
            >
              <option value="due_on_receipt">Due on Receipt</option>
              <option value="net_15">Net 15</option>
              <option value="net_30">Net 30</option>
              <option value="net_45">Net 45</option>
              <option value="net_60">Net 60</option>
            </select>
          </div>

          <div className="inv-modal-field">
            <label htmlFor="tax-rate" className="inv-modal-label">
              Tax Rate (%)
            </label>
            <input
              id="tax-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value))}
              placeholder="e.g. 8.25"
              className="inv-modal-input"
            />
          </div>

          <div className="inv-modal-field">
            <label htmlFor="notes" className="inv-modal-label">
              Notes (Optional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes or special instructions"
              className="inv-modal-textarea"
            />
          </div>

          <div className="inv-modal-preview">
            <div className="inv-modal-preview-row">
              <span>Base Amount:</span>
              <span>${baseAmount.toFixed(2)}</span>
            </div>
            {taxRate > 0 && (
              <div className="inv-modal-preview-row">
                <span>Tax ({taxRate}%):</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="inv-modal-preview-row inv-modal-preview-total">
              <span>Invoice Total:</span>
              <span>${totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </form>

        <div className="inv-modal-footer">
          <button
            type="button"
            onClick={handleClose}
            className="inv-modal-btn inv-modal-btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="inv-modal-btn inv-modal-btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  )
}
